import React from 'react'
import styled, { keyframes } from 'styled-components'
// Renders nothing unless GATSBY_ASK_URL is set at build time, so this is
// safe to ship before the Worker exists. See worker/README.md.
import AskWidget from '../components/AskWidget'
import AskBar from '../components/AskBar'
import PlaylistEmbed from '../components/PlaylistEmbed'
import { SealMark } from '../components/Marks.js'

const rise = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: none; }
`;

const Wrap = styled.div`
  max-width: 68rem;
  margin: 0 auto;
  padding: 0 1.5rem;

  @media (max-width: 34rem) { padding: 0 1.15rem; }
`;

const Eyebrow = styled.div`
  font-family: var(--face-mono);
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--brass);
  margin-bottom: 1.15rem;
`;

/* ---------- hero ---------- */

const Hero = styled.section`
  padding: 5rem 0 3.5rem;

  > * {
    animation: ${rise} 620ms cubic-bezier(0.22, 0.68, 0.35, 1) both;
  }
  > *:nth-child(1) { animation-delay: 60ms; }
  > *:nth-child(2) { animation-delay: 150ms; }
  > *:nth-child(3) { animation-delay: 240ms; }
  > *:nth-child(4) { animation-delay: 330ms; }

  @media (max-width: 34rem) { padding: 3rem 0 2.5rem; }
`;

/* Engraved capitals. Tracking is pulled back from the system's .19em here
   and only here: that value is set for label-scale type, and at display
   size it opens the line past the point where the words read as one unit.
   Everything at label scale keeps .19em. */
const HeroTitle = styled.h1`
  font-family: var(--face-display);
  font-weight: 500;
  font-size: clamp(2rem, 5.4vw, 3.6rem);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1.2;
  color: var(--vellum);
  max-width: 22ch;

  em {
    font-style: normal;
    color: var(--brass);
  }
`;

const Deck = styled.p`
  font-family: var(--face-prose);
  font-size: clamp(1.05rem, 2.1vw, 1.32rem);
  font-weight: 400;
  line-height: 1.55;
  color: var(--chalk);
  max-width: 54ch;
  margin-top: 1.75rem;
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.15rem 1.75rem;
  margin-top: 2.5rem;
`;

const Cta = styled.a`
  display: inline-block;
  font-family: var(--face-mono);
  font-size: 0.92rem;
  letter-spacing: 0.06em;
  padding: 0.95rem 1.6rem;
  /* White, not red. The ask launcher is fixed and therefore on screen at
     all times, so a red CTA would put two committing actions on every
     screen at once. Red belongs to the launcher; white on this ground is
     18.7:1 and reads as primary without competing with it. */
  color: var(--ink-deep);
  background: var(--accent-strong);
  border: 1px solid var(--accent-strong);
  transition: transform 160ms ease, background 160ms ease;

  &:hover {
    background: var(--vellum);
    border-color: var(--vellum);
    color: var(--ink-deep);
    transform: translateY(-2px);
  }

  /* secondary: outline, for the paired call */
  &.secondary {
    background: transparent;
    color: var(--brass);
    border-color: var(--brass);
  }

  &.secondary:hover {
    background: var(--brass);
    color: var(--ink-deep);
  }
`;

const CtaNote = styled.span`
  font-family: var(--face-mono);
  font-size: 0.8rem;
  color: var(--chalk-dim);
  line-height: 1.6;

  a {
    color: var(--brass);
    border-bottom: 1px solid rgba(196, 154, 58, 0.4);
  }

  a:hover { border-bottom-color: var(--brass); }
`;

/* ---------- signature: the dual-reading console ---------- */

const ConsoleSection = styled.section`
  padding: 2.5rem 0 4.5rem;
`;

const ConsoleLede = styled.p`
  font-family: var(--face-prose);
  max-width: 58ch;
  color: var(--chalk-dim);
  margin-bottom: 1.75rem;
`;

const ConsoleFrame = styled.div`
  position: relative;
  background: var(--ink-deep);
  border: 1px solid var(--ink-rule);
  border-radius: 2px;

  .reading-input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;
  }

  .tabs {
    display: flex;
    border-bottom: 1px solid var(--ink-rule);
  }

  .tabs label {
    font-family: var(--face-mono);
    font-size: 0.78rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 0.9rem 1.35rem;
    color: var(--chalk-dim);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: color 160ms ease, border-color 160ms ease;
  }

  .tabs label:hover { color: var(--vellum); }

  .pane { display: none; padding: 1.75rem; overflow-x: auto; }

  .pane pre {
    font-size: 0.88rem;
    line-height: 1.7;
    color: var(--chalk);
    white-space: pre;
  }

  .prompt { color: var(--verdigris); }
  .val { color: var(--flag-red-lit); }
  .prose { color: var(--vellum); }

  .caption {
    font-family: var(--face-mono);
    font-size: 0.74rem;
    line-height: 1.7;
    color: var(--chalk-dim);
    max-width: 68ch;
    padding: 0 1.75rem 1.5rem;
  }

  #read-player:checked ~ .tabs label[for="read-player"],
  #read-instrument:checked ~ .tabs label[for="read-instrument"] {
    color: var(--brass);
    border-bottom-color: var(--brass);
  }

  #read-player:checked ~ .panes .pane-player,
  #read-instrument:checked ~ .panes .pane-instrument {
    display: block;
  }

  #read-player:focus-visible ~ .tabs label[for="read-player"],
  #read-instrument:focus-visible ~ .tabs label[for="read-instrument"] {
    outline: 2px solid var(--brass);
    outline-offset: -4px;
  }

  @media (max-width: 34rem) {
    .pane { padding: 1.15rem; }
    .caption { padding: 0 1.15rem 1.15rem; }
    .pane pre { font-size: 0.78rem; }

    /* Prose should wrap on a narrow screen; the readout keeps its
       column alignment and scrolls instead. */
    .pane-player pre { white-space: pre-wrap; }
  }
`;

/* ---------- generic section ---------- */

/* Every section divider is a flag rule. Red works here because it is a
   field with white on both sides, not ink — true Old Glory Red is only
   2.9:1 on this ground and could never carry type. */
const Section = styled.section`
  position: relative;
  padding: calc(3.5rem + var(--flag-rule-height)) 0 3.5rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: var(--flag-rule-height);
    background: var(--flag-rule);
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--face-display);
  font-weight: 500;
  font-size: clamp(1.45rem, 2.8vw, 2rem);
  text-transform: uppercase;
  letter-spacing: 0.13em;
  line-height: 1.28;
  color: var(--vellum);
  max-width: 26ch;
`;

const Body = styled.p`
  font-family: var(--face-prose);
  max-width: 62ch;
  margin-top: 1.35rem;
  color: var(--chalk);
`;

/* ---------- the loop ---------- */

const LoopList = styled.ol`
  list-style: none;
  margin: 2.5rem 0 0;
  padding: 0;
  display: grid;
  gap: 1px;
  grid-template-columns: repeat(4, 1fr);
  background: var(--ink-rule);
  border: 1px solid var(--ink-rule);

  @media (max-width: 52rem) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 30rem) { grid-template-columns: 1fr; }
`;

const LoopStep = styled.li`
  background: var(--ink);
  padding: 1.6rem 1.35rem;

  h3 {
    font-family: var(--face-mono);
    font-size: 0.78rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--brass);
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: var(--chalk-dim);
    margin: 0;
  }
`;

const LoopNote = styled.p`
  font-family: var(--face-mono);
  font-size: 0.78rem;
  color: var(--chalk-dim);
  margin-top: 1.15rem;

  span { color: var(--brass); }
`;

/* the line under the playlist frame — same voice as CtaNote, block-level */
const SeriesNote = styled.p`
  font-family: var(--face-mono);
  font-size: 0.8rem;
  color: var(--chalk-dim);
  line-height: 1.6;
  margin-top: 1.15rem;

  a {
    color: var(--brass);
    border-bottom: 1px solid rgba(196, 154, 58, 0.4);
  }

  a:hover { border-bottom-color: var(--brass); }
`;

/* ---------- commitments (vellum cards) ---------- */

const Cards = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(2, 1fr);
  margin-top: 2.5rem;

  @media (max-width: 46rem) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: var(--vellum);
  color: var(--card-ink);
  padding: 2rem 1.85rem;
  border-radius: 2px;

  h3 {
    font-family: var(--face-display);
    font-weight: 500;
    font-size: 1.05rem;
    text-transform: uppercase;
    letter-spacing: var(--engraved-tracking);
    line-height: 1.3;
    color: var(--card-ink);
    margin-bottom: 0.9rem;
  }

  p {
    font-family: var(--face-prose);
    color: var(--card-ink-dim);
  }

  p:last-child { margin-bottom: 0; }

  strong { font-weight: 600; color: var(--card-ink); }
`;

const CardTag = styled.div`
  font-family: var(--face-mono);
  font-size: 0.7rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--card-ink-dim);
  margin-bottom: 1rem;
`;

/* ---------- chambers (three-up) ---------- */

const Chambers = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(3, 1fr);
  margin-top: 2.5rem;

  @media (max-width: 62rem) { grid-template-columns: 1fr; }
`;

/* ---------- transcript ---------- */

const Transcript = styled.pre`
  margin-top: 2.25rem;
  background: var(--ink-deep);
  border: 1px solid var(--ink-rule);
  border-left: 2px solid var(--verdigris);
  padding: 1.6rem 1.75rem;
  font-size: 0.88rem;
  line-height: 1.75;
  color: var(--chalk);
  overflow-x: auto;
  white-space: pre-wrap;

  .prompt { color: var(--verdigris); }

  @media (max-width: 34rem) {
    padding: 1.15rem;
    font-size: 0.78rem;
  }
`;

/* ---------- built with ---------- */

const Specs = styled.dl`
  a {
    color: var(--brass);
    border-bottom: 1px solid rgba(196, 154, 58, 0.4);
  }

  a:hover { border-bottom-color: var(--brass); }

  margin: 2.5rem 0 0;
  display: grid;
  grid-template-columns: 12rem 1fr;
  gap: 0;

  dt {
    font-family: var(--face-mono);
    font-size: 0.74rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--brass);
    padding: 1.15rem 0;
    border-top: 1px solid var(--ink-rule);
  }

  dd {
    margin: 0;
    padding: 1.15rem 0;
    border-top: 1px solid var(--ink-rule);
    color: var(--chalk);
  }

  @media (max-width: 40rem) {
    grid-template-columns: 1fr;

    dt { padding-bottom: 0.35rem; }
    dd { padding-top: 0.35rem; border-top: none; }
  }
`;

const Closing = styled.section`
  position: relative;
  padding: calc(3.5rem + var(--flag-rule-height)) 0 0.5rem;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: var(--flag-rule-height);
    background: var(--flag-rule);
  }
`;

/* The mark applied — this is a surface it does not own, so it wears the
   seal rather than the standalone rings. */
const ClosingSeal = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.85rem;

  svg {
    width: 84px;
    height: 84px;
  }

  @media (max-width: 34rem) {
    svg {
      width: 64px;
      height: 64px;
    }
  }
`;

const ClosingTitle = styled.h2`
  font-family: var(--face-display);
  font-weight: 500;
  font-size: clamp(1.3rem, 2.6vw, 1.8rem);
  text-transform: uppercase;
  letter-spacing: 0.13em;
  line-height: 1.3;
  color: var(--vellum);
  margin-bottom: 1.75rem;
`;

const IndexPage = () => (
  <Wrap>
    <Hero>
      <Eyebrow>Saxonberg &mdash; a multiplayer text world</Eyebrow>
      <HeroTitle>
        A world your community <em>actually runs</em>.
      </HeroTitle>
      <Deck>
        Underneath: a simulation with real units and real math, and no fudge
        anywhere in the substrate. On top: a written Compact where standing is
        earned by what you do, and every decision lands in a record anyone can
        read.
      </Deck>
      <CtaRow>
        <Cta href="https://mud.panterasbox.com">Play the live demo</Cta>
        <Cta href="#communities" className="secondary">Bring your community</Cta>
      </CtaRow>
      <CtaNote>
        mud.panterasbox.com &mdash; an early demo on one small box, and{' '}
        <strong>the world resets nightly</strong>. Nothing you do there is
        permanent yet, so come break it. New here?{' '}
        <a href="https://www.youtube.com/@BobbySchaetzle">Start with the videos</a>.
      </CtaNote>

      <AskBar />
    </Hero>

    <ConsoleSection>
      <Eyebrow>The same room, read two ways</Eyebrow>
      <ConsoleLede>
        A player just looks. A student points an instrument at it. Same engine,
        same fountain &mdash; only the rendering changed.
      </ConsoleLede>

      <ConsoleFrame>
        <input
          className="reading-input"
          type="radio"
          name="reading"
          id="read-player"
          defaultChecked
        />
        <input
          className="reading-input"
          type="radio"
          name="reading"
          id="read-instrument"
        />

        <div className="tabs">
          <label htmlFor="read-player">Player</label>
          <label htmlFor="read-instrument">Instrument</label>
        </div>

        <div className="panes">
          <div className="pane pane-player">
            <pre>
              <span className="prompt">&gt; look</span>
              {'\n'}
              <span className="prose">
                A fountain stands at the center of the courtyard, water trickling
                softly.
              </span>
            </pre>
          </div>

          <div className="pane pane-instrument">
            <pre>
              <span className="prompt">&gt; analyze sound here</span>
              {'\n'}Source: fountain{'\n'}
              {'  '}Emitted: <span className="val">30 dB SPL @ 100&ndash;800 Hz</span>
              {'\n'}
              {'  '}At your position: <span className="val">28 dB</span>
              {'\n'}Aggregate: <span className="val">28 dB SPL</span>
              {'\n'}Reverberation: <span className="val">0.6s</span> (living room)
            </pre>
          </div>
        </div>

        <div className="caption">
          The prose was never flavor painted over a fake. It is a faithful reading
          of a model that already holds real decibels and real frequencies.
        </div>
      </ConsoleFrame>
    </ConsoleSection>

    <Section>
      <Eyebrow>Who decides</Eyebrow>
      <SectionTitle>The people in it govern it.</SectionTitle>
      <Body>
        Saxonberg is run by its members under a written Compact, and the mechanism
        is specific rather than ceremonial. Standing is{' '}
        <strong>earned by what you actually do</strong>, and it measures a rate
        rather than a total &mdash; it fades when you stop. A position gains weight
        the longer you hold it, so a settled conviction outweighs a passing mood.
      </Body>

      <Chambers>
        <Card>
          <CardTag>01 &mdash; Make</CardTag>
          <h3>You built it</h3>
          <p>
            Standing from the work that becomes the world &mdash; content, code, and
            the systems underneath.
          </p>
        </Card>
        <Card>
          <CardTag>02 &mdash; Fund</CardTag>
          <h3>You keep it running</h3>
          <p>Standing from paying the bills. Standing, not equity.</p>
        </Card>
        <Card>
          <CardTag>03 &mdash; Play</CardTag>
          <h3>You live here</h3>
          <p>
            Standing from presence, and from what the people around you make of it.
          </p>
        </Card>
      </Chambers>

      <Body>
        A measure carries with <strong>two of the three</strong>. No chamber acts
        alone, and every act lands in an append-only record any member can read
        &mdash; which is what makes the rest checkable instead of promised.
      </Body>

      <Body>
        None of the parts are new. Multi-stakeholder cooperatives have seated
        labor, capital and users at the same table for thirty years, and
        conviction voting came out of the DAO world. What is unusual here is
        that <strong>standing is a rate rather than a membership card</strong>:
        you earn it by what you do, it fades when you stop, and one person can
        hold it in all three chambers at once.
      </Body>

      <Body>
        And it governs more than the fiction. A change to the world and a change
        to its law go through <strong>the same deliberation, the same standing,
        the same record</strong> &mdash; where almost everywhere else, the people
        affected by a change and the people who approve it are different
        populations on different machinery.{' '}
        <em>
          The engine itself stays gated: a community governs its world, not the
          runtime.
        </em>
      </Body>

      <Body>
        All of it, slowed down: seven chapters on how a proposal becomes a rule
        and a rule becomes running code. Chapter&nbsp;1 is the whole machine in
        one pass and stands on its own.
      </Body>

      <PlaylistEmbed />

      <SeriesNote>
        <a
          href="https://www.youtube.com/playlist?list=PLFFj5IiyqKDI"
          target="_blank"
          rel="noopener noreferrer"
        >
          The series on YouTube
        </a>{' '}
        &mdash; or jump to the part you want up close.
      </SeriesNote>
    </Section>

    <Section id="communities">
      <Eyebrow>For communities</Eyebrow>
      <SectionTitle>Bring one that already exists.</SectionTitle>
      <Body>
        A stream and its regulars. A guild. A class. A server that has outgrown
        its pinned-message constitution. You do not have to build an audience
        &mdash; you already have one, and it already argues about who decided
        what. Give it somewhere that remembers.
      </Body>

      <Chambers>
        <Card>
          <CardTag>Hosted</CardTag>
          <h3>We run it</h3>
          <p>
            An instance and a Compact of your own &mdash; nothing to deploy,
            nothing to maintain. <strong>This part is not built yet</strong>;
            the first few will be stood up by hand, with the people who want
            them.
          </p>
        </Card>
        <Card>
          <CardTag>Self-governed</CardTag>
          <h3>You write the rules</h3>
          <p>
            The Compact is content, not code. Amend it, seat your own offices,
            decide how standing is earned.
          </p>
        </Card>
        <Card>
          <CardTag>Self-hosted</CardTag>
          <h3>You take it with you</h3>
          <p>
            The whole engine is AGPL. Run it yourself, whenever you want, with
            everything your community built.
          </p>
        </Card>
      </Chambers>

      <Body>
        That last one is not a concession &mdash; it is the point.{' '}
        <strong>
          A community that cannot leave is not governing itself, it is being
          hosted.
        </strong>{' '}
        The dial runs from operator to republic, and it only turns one way.
      </Body>

      <CtaRow>
        <Cta href="https://discord.gg/meEtHsZT8M">Come talk to us on Discord</Cta>
        <CtaNote>
          Bring the community you already have, or just come see what it looks
          like.
        </CtaNote>
      </CtaRow>
    </Section>

    <Section>
      <Eyebrow>The keystone</Eyebrow>
      <SectionTitle>The human interface is the AI interface.</SectionTitle>
      <Body>
        The loop a player runs &mdash; read prose, decide, type a command &mdash; is
        exactly the loop a language model runs. So there is no separate AI adapter.
        An AI-driven character perceives through the same text channel, acts through
        the same command bus, and is gated by the same security as a human.
      </Body>

      <Transcript>
        <span className="prompt">&gt; say Does anyone have last week&rsquo;s lecture notes?</span>
        {'\n'}Priya raises her hand. &ldquo;I&rsquo;ve got them &mdash; I&rsquo;ll drop them in the channel.&rdquo;
        {'\n'}Devon shrugs. &ldquo;Mine are a disaster, sorry.&rdquo;
        {'\n'}Professor Albright nods. &ldquo;Good. Let&rsquo;s pick up where we left off.&rdquo;
      </Transcript>

      <Body>
        Any of those three might be running on a language model &mdash; and nothing in
        the engine, the prose, or the command bus knows or needs to know which. Because
        every action is a visible, attributable command, an agent&rsquo;s behavior is as
        auditable as a person&rsquo;s, and an agent can&rsquo;t do anything a skilled
        player couldn&rsquo;t.
      </Body>
    </Section>

    <Section>
      <Eyebrow>Two commitments</Eyebrow>
      <SectionTitle>Both readable and rigorous.</SectionTitle>

      <Cards>
        <Card>
          <CardTag>01 &mdash; The model is honest</CardTag>
          <h3>Numbers, not vibes</h3>
          <p>
            Where the engine models something measurable, it uses{' '}
            <strong>real units and real math</strong> &mdash; no 0&ndash;100 sliders
            or 1&ndash;5 star fudge anywhere in the substrate.
          </p>
          <p>
            A chemistry student inspecting a beaker sees real molarity. A biology
            student playing a dog has that species&rsquo; real hearing range. In-world
            instruments and the <code>analyze</code> verb reveal the numbers;
            everyone else just reads the sentence.
          </p>
        </Card>

        <Card>
          <CardTag>02 &mdash; The medium is text</CardTag>
          <h3>A feature, not nostalgia</h3>
          <p>
            Everything you perceive arrives as <strong>prose</strong>; everything you
            do is a <strong>command string</strong>. Text can represent anything &mdash;
            a smell, a legal argument, a chemical reaction.
          </p>
          <p>
            It dodges the uncanny valley, its ceiling is your imagination rather than
            an art budget, and it is social to the core. Graphics ride as a
            decorational layer over the text, never under it.
          </p>
        </Card>
      </Cards>
    </Section>

    <Section>
      <Eyebrow>How it works</Eyebrow>
      <SectionTitle>Learning feeds play. Play motivates learning.</SectionTitle>
      <Body>
        One loop, not a quiz bolted onto a game. Your character grows in a way that
        matches what you actually did.
      </Body>

      <LoopList>
        <LoopStep>
          <h3>You practice</h3>
          <p>Study or practice something real, outside the game.</p>
        </LoopStep>
        <LoopStep>
          <h3>The game notices</h3>
          <p>That engagement becomes a signal the world can read.</p>
        </LoopStep>
        <LoopStep>
          <h3>Your character grows</h3>
          <p>Advancement matches the shape of what you did.</p>
        </LoopStep>
        <LoopStep>
          <h3>The world opens</h3>
          <p>New capabilities &mdash; and new reasons to go learn the next thing.</p>
        </LoopStep>
      </LoopList>
      <LoopNote>
        <span>&#8635;</span> and the loop closes &mdash; each turn hands you the next
        reason to begin again.
      </LoopNote>
    </Section>

    <Section>
      <Eyebrow>Under the hood</Eyebrow>
      <SectionTitle>What it&rsquo;s made of.</SectionTitle>

      <Specs>
        <dt>World model</dt>
        <dd>
          A shallow spine of fundamental kinds &mdash; Idea, Thing, Location, Vessel,
          Agent &mdash; crossed with composable traits like Container, LightSource and
          Atmospheric. A torch, a lantern and a glowing sword aren&rsquo;t three
          classes; they&rsquo;re three combinations.
        </dd>

        <dt>Server</dt>
        <dd>TypeScript, Node and Express, WebSockets, MongoDB.</dd>

        <dt>Client</dt>
        <dd>
          React and Vite &mdash; a multi-panel cockpit with a dominant text console and
          a contextual inspection pane. Every UI gesture composes a command string and
          submits it to the same bus the CLI uses.
        </dd>

        <dt>Status</dt>
        <dd>
          The engine foundations are largely in place. The gamification layer and the
          vertical-integration tier are what&rsquo;s ahead, and combat is in design now.
          The public instance is an early demo, not a finished game.
        </dd>

        <dt>The demo, honestly</dt>
        <dd>
          One small box, a known memory leak or two, and the database is wiped
          nightly. A permanent instance gets minted when there is somebody on it
          besides us &mdash; until then the reset is doing useful work. If you
          find it down or broken,{' '}
          <a href="https://discord.gg/meEtHsZT8M">say so in Discord</a>; that is
          genuinely the most useful thing you can do right now.
        </dd>
      </Specs>
    </Section>

    <Closing>
      <ClosingSeal>
        <SealMark id="closing-seal" title="Saxonberg" />
      </ClosingSeal>
      <ClosingTitle>Come see it running.</ClosingTitle>
      <CtaRow style={{ justifyContent: 'center' }}>
        <Cta href="https://mud.panterasbox.com">Play the live demo</Cta>
      </CtaRow>
    </Closing>

    <AskWidget />
  </Wrap>
)

export default IndexPage
